import {
  ConsistencyRuleWidgetProps,
  DailyLimitWidgetProps,
  EODTrailingLevelWidgetProps,
  InactiveDaysWidgetProps,
  MaxLossWidgetProps,
  PayoutWidgetProps,
  ProfitableDaysWidgetProps,
  WidgetProps,
  WidgetType,
} from '@hub/features/account-dashboard/types/widget-manager.types';
import { ConsistencyRuleObjective } from '../components/consistency-rule-objective';
import { DailyLimitObjective } from '../components/daily-limit-objective';
import { EODTrailingLevelObjective } from '../components/eod-trailing-level-objective';
import { InactiveDaysObjective } from '../components/inactive-days-objective';
import { MaxLossObjective } from '../components/max-loss-objective';
import { PayoutObjective } from '../components/payout-objective';
import { ProfitableDaysObjective } from '../components/profitable-days-objective';
import { ObjectiveData } from '../types';

/**
 * Widget component type
 */
type WidgetComponent = (props: WidgetProps) => ObjectiveData | null;

/**
 * Wraps widget component with error handling
 * @param component - Widget component function
 * @param props - Widget props
 * @returns ObjectiveData or null if error occurred
 */
const renderWidgetWithErrorHandling = (
  component: WidgetComponent,
  props: WidgetProps,
): ObjectiveData | null => {
  try {
    return component(props);
  } catch {
    // Error handled silently - widget will not render
    return null;
  }
};

/**
 * Widget registry map for O(1) lookup
 * Maps widget types to their component functions
 */
const WIDGET_REGISTRY: Record<WidgetType, WidgetComponent> = {
  [WidgetType.ProfitableDays]: (props) =>
    ProfitableDaysObjective(props as ProfitableDaysWidgetProps),
  [WidgetType.InactiveDays]: (props) =>
    InactiveDaysObjective(props as InactiveDaysWidgetProps),
  [WidgetType.MaxLoss]: (props) =>
    MaxLossObjective(props as MaxLossWidgetProps),
  [WidgetType.DailyLimit]: (props) =>
    DailyLimitObjective(props as DailyLimitWidgetProps),
  [WidgetType.Payout]: (props) => PayoutObjective(props as PayoutWidgetProps),
  [WidgetType.EODTrailingLevel]: (props) =>
    EODTrailingLevelObjective(props as EODTrailingLevelWidgetProps),
  [WidgetType.ConsistencyRule]: (props) =>
    ConsistencyRuleObjective(props as ConsistencyRuleWidgetProps),
};

/**
 * Map widget type and props to ObjectiveData component
 * @param widgetType - Widget type identifier
 * @param props - Widget props
 * @returns ObjectiveData or null if widget not found or error occurred
 */
export const mapWidgetToComponent = (
  widgetType: WidgetType,
  props: WidgetProps,
): ObjectiveData | null => {
  // Get widget component from registry immediately
  const widgetComponent = WIDGET_REGISTRY[widgetType];

  // Combined guard: check widgetType and widgetComponent
  if (!widgetType || !widgetComponent || !props) {
    return null;
  }

  // Render widget component with error handling
  return renderWidgetWithErrorHandling(widgetComponent, props);
};

/**
 * Export registry for external access if needed
 */
export { WIDGET_REGISTRY };
